/**
 * Edit mode's state (DOP-622): the whole memo as one document, what has
 * changed since editing started, and a draft kept on this computer.
 *
 * The document is edited as one piece. Saving cuts it back into sections at
 * the fixed headings (`splitMemoDoc`), and each section that changed becomes
 * one edit script. The financial summary's figures are kept beside it
 * (DOP-637): they are the memo's facts, not its text.
 *
 * The draft is a convenience, not storage. It lives in this browser's local
 * storage under the version being edited, so a reload or a closed tab does
 * not lose an hour of edits. Nobody else sees it, and it is dropped on save
 * or discard. Reading and writing it never throws: in a private window the
 * edits still work, they just do not survive a reload.
 */

import type { JSONContent } from "@tiptap/react";
import { useCallback, useMemo, useRef, useState } from "react";

import {
	type AddedSection,
	type MemoChange,
	isEditableSection,
	itemsFromDoc,
	itemsKey,
	memoDoc,
	sectionChanges,
	sectionChangesOf,
	splitMemoDoc,
	titlesOf,
	withSectionDoc,
} from "../components/DealScreening/memoEditorModel";
import {
	EMPTY_FIGURES,
	type FiguresDraft,
	figuresFromStored,
	hasFigureEdits,
} from "../components/DealScreening/figuresEditModel";
import { memoSections } from "../components/DealScreening/memoModel";
import type { EditItem } from "../components/DealScreening/sectionEditModel";
import type { DealScreeningRun, DealScreeningSection } from "../types/dealScreening";

const draftKey = (runId: string) => `doppler.dealScreening.editDraft.${runId}`;

/**
 * The stored draft. `doc` is the whole memo. `docs` (one document per
 * section) is how drafts were kept before the memo became one document, and
 * is still read.
 */
export interface StoredDraft {
	doc?: JSONContent;
	docs?: Record<string, JSONContent>;
	/** The financial summary as typed (DOP-637). */
	figures?: FiguresDraft;
	savedAt: string;
}

export const readMemoEditDraft = (runId: string): StoredDraft | null => {
	try {
		const raw = window.localStorage.getItem(draftKey(runId));
		if (!raw) return null;
		const parsed = JSON.parse(raw) as StoredDraft;
		return parsed && (typeof parsed.doc === "object" || typeof parsed.docs === "object")
			? parsed
			: null;
	} catch {
		return null;
	}
};

export const writeMemoEditDraft = (
	runId: string,
	doc: JSONContent,
	figures: FiguresDraft = EMPTY_FIGURES,
): boolean => {
	try {
		window.localStorage.setItem(
			draftKey(runId),
			JSON.stringify({ doc, figures, savedAt: new Date().toISOString() }),
		);
		return true;
	} catch {
		return false;
	}
};

export const clearMemoEditDraft = (runId: string): void => {
	try {
		window.localStorage.removeItem(draftKey(runId));
	} catch {
		// Nothing stored, or storage blocked: nothing to clear.
	}
};

/** What a section a person cannot type in says instead, in the document. */
export const readOnlyNote = (section: DealScreeningSection): string =>
	section.status === "omitted"
		? "Left out of this memo: the files do not cover it."
		: section.key === "financial_summary"
			? "The financial summary is built from the figures Doppler found."
			: section.key === "diligence_questions"
				? "The questions are built from what is missing or unclear. Answer one on the Gaps tab to change them."
				: "Built by Doppler from the figures. Not edited here.";

/**
 * The memo's sections as edit mode opens them (DOP-637). The questions are
 * built from the gaps and have no text of their own until a person writes
 * them, so they open as the bullet list the reader sees: "Topic. Question".
 * What the person leaves becomes the section's text, in place of the list
 * built from the gaps.
 */
export const editSections = (run: DealScreeningRun): DealScreeningSection[] =>
	memoSections(run.sections).map((section) =>
		section.key === "diligence_questions" && section.paragraphs.length === 0 && run.questions.length > 0
			? {
					...section,
					paragraphs: [
						{
							anchor: null,
							lead: null,
							sub_heading: null,
							kind: "bullets",
							sentences: [...run.questions]
								.sort((a, b) => a.sort_order - b.sort_order)
								.map((question, index) => ({
									id: `diligence_questions:q:${index}`,
									text: `${question.topic.trim().replace(/\.$/, "")}. ${question.text.trim()}`,
									source: question.source,
									fact_keys: [],
								})),
						},
					],
				}
			: section,
	);

/** The memo as the document edit mode opens on. */
export const startMemoDoc = (
	run: DealScreeningRun,
	numberOf: (anchor: string | null | undefined) => number | null,
): JSONContent => memoDoc(editSections(run), numberOf, readOnlyNote);

/** A stored draft as a whole document, whichever way it was stored. */
export const draftDoc = (stored: StoredDraft | null, start: JSONContent): JSONContent | null => {
	if (!stored) return null;
	if (stored.doc) return stored.doc;
	let doc = start;
	for (const [key, part] of Object.entries(stored.docs ?? {})) doc = withSectionDoc(doc, key, part);
	return doc;
};

export interface MemoEditDraft {
	/** The sections a person can type in. */
	sections: DealScreeningSection[];
	/** The whole memo as it read when editing started. */
	startDoc: JSONContent;
	/** Each editable section's part of `startDoc`, for undoing one change. */
	startParts: Record<string, JSONContent>;
	/** What the editor opens on: the start, or a restored draft. */
	initialDoc: JSONContent;
	/** The document as it stands now. */
	doc: JSONContent;
	/** A draft from an earlier visit was restored. */
	restored: boolean;
	setDoc: (doc: JSONContent) => void;
	/** The financial summary as typed (DOP-637). */
	figures: FiguresDraft;
	setFigures: (next: FiguresDraft) => void;
	/** The current items of each section, for saving. */
	itemsBySection: Record<string, EditItem[]>;
	/** Sections whose text differs from the start. */
	dirtyKeys: string[];
	/** Each section's title as typed (DOP-641). */
	titles: Record<string, string>;
	/** Sections whose title differs from the start, any section (DOP-641). */
	renamedKeys: string[];
	/** Sections added in this draft, and the person's own sections taken out (DOP-656). */
	newSections: AddedSection[];
	removedSections: string[];
	/** Whether there is anything to save: a section changed, or a figure typed. */
	dirty: boolean;
	changes: MemoChange[];
	/** When the draft was last kept on this computer, or null if it could not be. */
	keptAt: Date | null;
	discard: () => void;
}

export function useMemoEditDraft(
	run: DealScreeningRun,
	numberOf: (anchor: string | null | undefined) => number | null,
	/**
	 * The document lives in the shared draft (memo co-editing): nothing is
	 * kept on this computer, and the editor opens on the shared document,
	 * not on a stored one.
	 */
	shared = false,
): MemoEditDraft {
	const sections = useMemo(
		() => editSections(run).filter(isEditableSection),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[run.sections, run.questions],
	);
	const startDoc = useMemo(() => startMemoDoc(run, numberOf), [run, numberOf]);
	const startParts = useMemo(() => splitMemoDoc(startDoc), [startDoc]);
	const startItems = useMemo(
		() =>
			Object.fromEntries(
				sections.map((s) => [s.key, itemsFromDoc(s, startParts[s.key] ?? { type: "doc" })]),
			),
		[sections, startParts],
	);

	// Read once, when edit mode opens.
	const [stored] = useState(() => (shared ? null : readMemoEditDraft(run.public_id)));
	const [initialDoc] = useState(() => draftDoc(stored, startDoc) ?? startDoc);
	const [doc, setDocState] = useState<JSONContent>(initialDoc);
	const [figures, setFiguresState] = useState<FiguresDraft>(() => figuresFromStored(stored?.figures));
	const [keptAt, setKeptAt] = useState<Date | null>(null);
	// The latest of each, for writing the other beside it.
	const docRef = useRef(doc);
	const figuresRef = useRef(figures);

	const setDoc = useCallback(
		(next: JSONContent) => {
			docRef.current = next;
			setDocState(next);
			if (!shared && writeMemoEditDraft(run.public_id, next, figuresRef.current)) setKeptAt(new Date());
		},
		[run.public_id, shared],
	);
	const setFigures = useCallback(
		(next: FiguresDraft) => {
			figuresRef.current = next;
			setFiguresState(next);
			if (!shared && writeMemoEditDraft(run.public_id, docRef.current, next)) setKeptAt(new Date());
		},
		[run.public_id, shared],
	);

	const itemsBySection = useMemo(() => {
		const parts = splitMemoDoc(doc);
		return Object.fromEntries(
			sections.map((s) => [s.key, itemsFromDoc(s, parts[s.key] ?? { type: "doc" })]),
		);
	}, [sections, doc]);
	const dirtyKeys = useMemo(
		() =>
			sections
				.filter((s) => itemsKey(itemsBySection[s.key]) !== itemsKey(startItems[s.key]))
				.map((s) => s.key),
		[sections, itemsBySection, startItems],
	);
	// Titles typed in place (DOP-641): every section's, the financial
	// summary's too. Left empty, a title keeps what it had.
	const startTitles = useMemo(() => titlesOf(startDoc), [startDoc]);
	const titles = useMemo(() => titlesOf(doc), [doc]);
	const renamedKeys = useMemo(
		() =>
			Object.keys(titles).filter(
				(key) => titles[key].trim() !== "" && titles[key].trim() !== (startTitles[key] ?? "").trim(),
			),
		[titles, startTitles],
	);
	const { added: newSections, removed: removedSections } = useMemo(
		() => sectionChangesOf(run, doc),
		[run, doc],
	);
	const changes = useMemo(
		() => [
			...newSections.map(
				(added): MemoChange => ({
					sectionKey: added.key,
					sectionTitle: added.title,
					kind: "added",
					anchor: null,
					summary: `Section "${added.title}" added`,
					text: added.title,
					newNumbers: [],
					section: true,
				}),
			),
			...removedSections.map((key): MemoChange => {
				const section = run.sections.find((s) => s.key === key);
				return {
					sectionKey: key,
					sectionTitle: section?.title ?? key,
					kind: "removed",
					anchor: null,
					summary: `Section "${section?.title ?? key}" taken out`,
					text: section?.title ?? key,
					newNumbers: [],
					section: true,
				};
			}),
			...memoSections(run.sections)
				.filter((s) => renamedKeys.includes(s.key))
				.map(
					(s): MemoChange => ({
						sectionKey: s.key,
						sectionTitle: titles[s.key].trim(),
						kind: "edited",
						anchor: null,
						summary: `Renamed "${startTitles[s.key]}" to "${titles[s.key].trim()}"`,
						text: titles[s.key].trim(),
						newNumbers: [],
						rename: true,
					}),
				),
			...sections.flatMap((s) =>
				dirtyKeys.includes(s.key)
					? sectionChanges(s, startItems[s.key], itemsBySection[s.key])
					: [],
			),
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[sections, dirtyKeys, startItems, itemsBySection, renamedKeys, titles, startTitles, run.sections, newSections, removedSections],
	);

	const discard = useCallback(() => clearMemoEditDraft(run.public_id), [run.public_id]);

	return {
		sections,
		startDoc,
		startParts,
		initialDoc,
		doc,
		restored: stored !== null,
		setDoc,
		figures,
		setFigures,
		itemsBySection,
		dirtyKeys,
		titles,
		renamedKeys,
		newSections,
		removedSections,
		dirty:
			dirtyKeys.length > 0 ||
			renamedKeys.length > 0 ||
			newSections.length > 0 ||
			removedSections.length > 0 ||
			hasFigureEdits(figures),
		changes,
		keptAt,
		discard,
	};
}
